import { useState } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { Button, Input } from '@lms/ui';
import type { Evaluation, EvaluationType } from '@lms/shared/types';
import { useUpdateEvaluation } from '../hooks/useEvaluations';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';

interface EvaluationModalProps {
  evaluation: Evaluation;
  onClose: () => void;
}

export function EvaluationModal({ evaluation, onClose }: EvaluationModalProps) {
  const { mutate: update, isPending } = useUpdateEvaluation();
  const { t } = useLanguage();

  const [formData, setFormData] = useState<Partial<Evaluation>>({
    title: evaluation.title,
    description: evaluation.description || '',
    type: evaluation.type,
    passingScore: evaluation.passingScore,
    maxAttempts: evaluation.maxAttempts,
    timeLimit: evaluation.timeLimit,
    numberOfQuestions: evaluation.numberOfQuestions,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    update(
      { id: evaluation.id, ...formData },
      {
        onSuccess: () => onClose(),
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-on-surface/40 backdrop-blur-sm z-[60] flex justify-center p-4 animate-fadeIn overflow-y-auto py-12">
      <div className="bg-surface-container-lowest rounded-[2rem] w-full max-w-xl shadow-level4 overflow-hidden animate-slideUp border border-on-surface/5 my-auto">
        <div className="p-8 border-b border-outline-variant/10 bg-surface-container-low/30 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-headline font-bold text-on-surface m-0 tracking-tight">
              <Bilingual {...copy.evaluationModal.title} />
            </h2>
            <p className="text-xs font-body text-on-surface-variant mt-1 opacity-60 uppercase tracking-widest font-bold">
              <Bilingual {...copy.evaluationModal.subtitle} />
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-on-surface/10 rounded-full transition-colors"
          >
            <X size={20} className="text-on-surface-variant" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="space-y-1.5">
            <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
              <Bilingual {...copy.evaluationModal.labelTitle} />
            </label>
            <Input
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              placeholder={t(copy.evaluationModal.placeholderTitle)}
              className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
              required
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
              <Bilingual {...copy.evaluationModal.labelContext} />
            </label>
            <textarea
              className="w-full bg-surface-container-low rounded-2xl px-5 py-4 text-sm font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 transition-all outline-none resize-none h-24"
              value={formData.description || ''}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder={t(copy.evaluationModal.placeholderContext)}
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
                <Bilingual {...copy.evaluationModal.labelCategory} />
              </label>
              <select
                className="w-full bg-surface-container-low rounded-xl px-4 py-3 text-sm font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 transition-all outline-none appearance-none h-12"
                value={formData.type}
                onChange={e => {
                  const newType = e.target.value as EvaluationType;
                  setFormData({
                    ...formData,
                    type: newType,
                    maxAttempts: newType === 'QUIZ' ? 3 : 1,
                  });
                }}
                required
              >
                <option value="QUIZ">{t(copy.evaluationModal.categoryQuiz)}</option>
                <option value="EXAM">{t(copy.evaluationModal.categoryExam)}</option>
                <option value="SURVEY">{t(copy.evaluationModal.categorySurvey)}</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
                <Bilingual {...copy.evaluationModal.labelPassing} />
              </label>
              <Input
                type="number"
                value={formData.passingScore}
                onChange={e => setFormData({ ...formData, passingScore: parseInt(e.target.value) })}
                className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
                <Bilingual {...copy.evaluationModal.labelAttempts} />
              </label>
              <Input
                type="number"
                value={formData.maxAttempts}
                onChange={e => setFormData({ ...formData, maxAttempts: parseInt(e.target.value) })}
                className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
                <Bilingual {...copy.evaluationModal.labelTimeLimit} />
              </label>
              <Input
                type="number"
                value={formData.timeLimit || ''}
                onChange={e =>
                  setFormData({
                    ...formData,
                    timeLimit: e.target.value ? parseInt(e.target.value) : null,
                  })
                }
                placeholder={t(copy.evaluationModal.placeholderTimeLimit)}
                className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
              <Bilingual {...copy.evaluationModal.labelRandomPool} />
            </label>
            <Input
              type="number"
              min="1"
              value={formData.numberOfQuestions ?? ''}
              onChange={e =>
                setFormData({
                  ...formData,
                  numberOfQuestions: e.target.value ? parseInt(e.target.value) : null,
                })
              }
              placeholder={t(copy.evaluationModal.placeholderRandomPool)}
              className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
            />
            <p className="text-[10px] text-on-surface-variant opacity-60 pl-2">
              <Bilingual {...copy.evaluationModal.descRandomPool} />
            </p>
          </div>

          <div className="pt-6 flex justify-end gap-6 items-center border-t border-outline-variant/10">
            <button
              type="button"
              onClick={onClose}
              className="text-[10px] uppercase font-black tracking-[0.2em] text-on-surface-variant hover:text-primary transition-colors"
            >
              <Bilingual {...copy.evaluationModal.abort} />
            </button>
            <Button
              type="submit"
              className="rounded-2xl px-10 h-14 text-[10px] uppercase font-black tracking-[0.25em] shadow-level2"
              disabled={isPending}
            >
              {isPending ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              <Bilingual {...copy.evaluationModal.submit} />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
